import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelDcr, changeDcrStatus, createDcr, createDcrComment, executeDcr, fetchDcr, fetchDcrComments, fetchDcrConnectorVersion, fetchDcrList, updateDcr, } from '../../utils';
const DCR_QUERY_KEY = 'dataChangeRequests';
export const dcrQueryKeys = {
    list: (environmentId, params) => [DCR_QUERY_KEY, 'list', environmentId, params],
    detail: (environmentId, dcrId) => [DCR_QUERY_KEY, 'detail', environmentId, dcrId],
    comments: (environmentId, dcrId) => [DCR_QUERY_KEY, 'comments', environmentId, dcrId],
    connectorVersion: (environmentId) => [DCR_QUERY_KEY, 'connectorVersion', environmentId],
};
export const useDcrList = ({ apiInstance, environmentId }, params = {}) => useQuery({
    queryKey: dcrQueryKeys.list(environmentId, params),
    queryFn: () => fetchDcrList(apiInstance, params),
    enabled: !!environmentId,
    placeholderData: keepPreviousData,
});
export const useDcrDetail = ({ apiInstance, environmentId }, dcrId) => useQuery({
    queryKey: dcrQueryKeys.detail(environmentId, dcrId),
    queryFn: () => fetchDcr(apiInstance, dcrId),
    enabled: !!environmentId && !!dcrId,
});
export const useDcrComments = ({ apiInstance, environmentId }, dcrId) => useQuery({
    queryKey: dcrQueryKeys.comments(environmentId, dcrId),
    queryFn: () => fetchDcrComments(apiInstance, dcrId),
    enabled: !!environmentId && !!dcrId,
});
/** The deployed connector's version only changes on deployment, so it is fetched once and kept */
export const useDcrConnectorVersion = ({ apiInstance, environmentId }) => useQuery({
    queryKey: dcrQueryKeys.connectorVersion(environmentId),
    queryFn: () => fetchDcrConnectorVersion(apiInstance),
    enabled: !!environmentId,
    staleTime: Infinity,
});
/** Invalidates every DCR query for the environment; list rows carry status and note, so a
 * mutation on one request can change what the list shows. */
const useInvalidateDcrQueries = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: [DCR_QUERY_KEY] });
};
export const useCreateDcr = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ payload, signature }) => createDcr(apiInstance, payload, signature),
        onSuccess: invalidate,
    });
};
export const useUpdateDcr = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ dcrId, payload, signature, }) => updateDcr(apiInstance, dcrId, payload, signature),
        onSuccess: invalidate,
    });
};
export const useCancelDcr = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ dcrId, note, signature }) => cancelDcr(apiInstance, dcrId, note, signature),
        onSuccess: invalidate,
    });
};
export const useChangeDcrStatus = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ dcrId, status, action, note }) => changeDcrStatus(apiInstance, dcrId, status, { action, note }),
        onSuccess: invalidate,
    });
};
export const useExecuteDcr = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ dcrId, signature }) => executeDcr(apiInstance, dcrId, signature),
        onSuccess: invalidate,
    });
};
export const useCreateDcrComment = ({ apiInstance }) => {
    const invalidate = useInvalidateDcrQueries();
    return useMutation({
        mutationFn: ({ dcrId, body }) => createDcrComment(apiInstance, dcrId, body),
        onSuccess: invalidate,
    });
};
